import React from 'react';
import { api } from '../api.js';
import { Empty, Loading, Modal, PH, Panel } from './components.jsx';
import { Button, Checkbox, DataTable, Field, Pill } from './ds/fgcp-ds.jsx';
import * as T from '../time.js';

/**
 * 관리 탭 '메일 에이전트'(계획 문서 5.6) — 업무 유형별 기능 만들기·수정·승인·중지(관리자). 승인된 기능을 수정하면 다시 승인 대기.
 * 처리 내용은 보지 않는다 — 켠 사람 수·처리 건수만. 도구는 서버의 도구 목록 안에서 고른다.
 */
const STATUS = { proposed: ['승인 대기', 'warn'], approved: ['승인됨', 'ok'], stopped: ['중지', 'neutral'] };
const blank = { key: '', name: '', description: '', instructions: '', tools: [], targetDepartmentIds: [], targetRoleCodes: [], proposalReason: '', calendarAuto: false };

export function MailAgentAdmin({ onToast }) {
  const [d, setD] = React.useState(null);
  const [depts, setDepts] = React.useState([]);
  const [roles, setRoles] = React.useState([]);
  const [form, setForm] = React.useState(null);
  const [gw, setGw] = React.useState(null);
  const load = () => {
    api.get('/api/v1/admin/mail-agent/capabilities').then(setD).catch(e => onToast(e.message));
    api.get('/api/v1/admin/mail-agent/gateway').then(setGw).catch(e => onToast(e.message));
  };
  React.useEffect(() => {
    load();
    api.get('/api/v1/org/departments').then(r => setDepts(r.departments)).catch(e => onToast(e.message));
    api.get('/api/v1/admin/roles').then(r => setRoles(r.roles)).catch(e => onToast(e.message));
  }, []);
  if (!d) return <Loading title="불러오는 중" />;
  const act = (path, msg) => api.post(path, {}).then(() => { onToast(msg); load(); }).catch(e => onToast(e.message));
  const save = async () => {
    const body = { ...form, proposalReason: form.proposalReason.trim() || null };
    delete body.id;
    try {
      if (form.id) await api.put(`/api/v1/admin/mail-agent/capabilities/${form.id}`, body); else await api.post('/api/v1/admin/mail-agent/capabilities', body);
      onToast(form.id ? '수정했습니다 — 다시 승인해야 적용됩니다' : '만들었습니다 — 승인해야 켤 수 있습니다'); setForm(null); load();
    } catch (e) { onToast(e.message); }
  };
  const toggle = (k, v) => setForm(f => {
    const next = { ...f, [k]: f[k].includes(v) ? f[k].filter(x => x !== v) : [...f[k], v] };
    return k === 'tools' && !next.tools.includes('propose.calendar') ? { ...next, calendarAuto: false } : next;
  });
  const deptName = id => depts.find(x => x.id === id)?.name ?? id;
  return (
    <Panel scroll>
      <PH title="메일 에이전트 기능" sub="업무 유형별 기능 — 승인한 기능만 대상 사용자가 켤 수 있고, 켠 사람 메일에만 적용됩니다. 처리 내용은 본인만 봅니다"
        right={<Button size="sm" onClick={() => setForm({ ...blank })}>+ 기능</Button>} />
      {gw && <GatewayStatus gw={gw} />}
      {d.capabilities.length === 0 ? <Empty title="기능이 없습니다" sub="기능을 만들고 승인하면 대상 사용자에게 '메일 에이전트' 메뉴가 보입니다" /> :
        <DataTable columns={[
          { key: 'name', header: '기능', strong: true, render: c => <span data-mail-cap-row={c.key}>{c.name} <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{c.key} · v{c.version}{c.proposed_by === 'agent' ? ' · 에이전트 제안' : ''}</span></span> },
          { key: 'target', header: '대상', render: c => <span style={{ fontSize: 11.5 }}>{[...c.target_department_ids.map(deptName), ...c.target_role_codes].join(', ') || '전 직원'}</span> },
          { key: 'tools', header: '도구', render: c => <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{c.tools.join(', ')}{c.calendar_auto ? ' · 일정 자동 등록 허용' : ''}</span> },
          { key: 'use', header: '켠 사람·처리', width: 100, render: c => <span className="mono" style={{ fontSize: 11.5 }}>{c.enrolled}명 · {c.items}건</span> },
          { key: 'status', header: '상태', width: 90, render: c => { const [l, t] = STATUS[c.status]; return <Pill tone={t} dot data-mail-cap-status={c.status}>{l}</Pill>; } },
          { key: 'act', header: '', width: 170, render: c => <div style={{ display: 'flex', gap: 4 }}>
            {c.actions.approve && <Button size="sm" onClick={() => act(`/api/v1/admin/mail-agent/capabilities/${c.id}/approve`, '승인했습니다')}>승인</Button>}
            {c.actions.edit && <Button size="sm" variant="secondary" onClick={() => setForm({ id: c.id, key: c.key, name: c.name, description: c.description, instructions: c.instructions,
              tools: c.tools, targetDepartmentIds: c.target_department_ids, targetRoleCodes: c.target_role_codes, proposalReason: c.proposal_reason ?? '', calendarAuto: c.calendar_auto })}>수정</Button>}
            {c.actions.stop && <Button size="sm" variant="secondary" onClick={() => { if (window.confirm(`'${c.name}'을(를) 중지할까요? 켠 사람 모두 꺼지고 처리 기록은 남습니다.`)) act(`/api/v1/admin/mail-agent/capabilities/${c.id}/stop`, '중지했습니다'); }}>중지</Button>}
          </div> },
        ]} rows={d.capabilities} />}
      {form && (
        <Modal width={600} title={form.id ? '기능 수정' : '기능 만들기'} icon="inbox" onClose={() => setForm(null)} footer={<>
          <Button variant="secondary" size="sm" onClick={() => setForm(null)}>취소</Button>
          <Button size="sm" disabled={!form.key || !form.name.trim()} onClick={save}>{form.id ? '저장(다시 승인 필요)' : '만들기'}</Button></>}>
          <div data-mail-cap-form style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12 }}>
              <Field label="식별자" value={form.key} disabled={Boolean(form.id)} maxLength={41} onChange={e => setForm({ ...form, key: e.target.value.trim() })} placeholder="예: sales-mail" />
              <Field label="이름" value={form.name} maxLength={60} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <Field label="설명(사용자에게 보임)" as="textarea" value={form.description} maxLength={1000} onChange={e => setForm({ ...form, description: e.target.value })} />
            <Field label="에이전트 지침(이 업무를 어떻게 이해하고 무엇을 제안할지)" as="textarea" value={form.instructions} maxLength={8000} onChange={e => setForm({ ...form, instructions: e.target.value })} />
            <div><div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>도구</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {Object.entries(d.tools).map(([k, label]) => <Checkbox key={k} size="sm" label={<span className="mono">{k}</span>} description={label}
                  checked={form.tools.includes(k)} onChange={() => toggle('tools', k)} />)}
              </div></div>
            <Checkbox data-mail-cap-calendar-auto label="일정 자동 등록 허용" checked={form.calendarAuto} disabled={!form.tools.includes('propose.calendar')}
              description="켠 사람이 자동 등록도 켜면, 에이전트가 찾은 일정을 그 사람 캘린더에 바로 넣습니다(회사 밖 메일 포함, propose.calendar 필요)"
              onChange={v => setForm({ ...form, calendarAuto: v })} />
            <div><div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>대상 부서(하위 포함) — 부서·역할 모두 비우면 전 직원</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 120, overflow: 'auto' }}>
                {depts.map(x => <Checkbox key={x.id} size="sm" label={x.name} checked={form.targetDepartmentIds.includes(x.id)} onChange={() => toggle('targetDepartmentIds', x.id)} />)}
              </div></div>
            <div><div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>대상 역할</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 100, overflow: 'auto' }}>
                {roles.map(r => <Checkbox key={r.code} size="sm" label={r.name} checked={form.targetRoleCodes.includes(r.code)} onChange={() => toggle('targetRoleCodes', r.code)} />)}
              </div></div>
            <Field label="제안 사유·메모" as="textarea" value={form.proposalReason} maxLength={1000} onChange={e => setForm({ ...form, proposalReason: e.target.value })} />
          </div>
        </Modal>
      )}
    </Panel>
  );
}

const WRITER = { none: ['일정 쓰기 앱 없음', 'neutral'], preparing: ['일정 쓰기 앱 준비 중', 'warn'], ready: ['일정 쓰기 앱 준비됨', 'ok'] };
const READER = { none: ['메일 읽기 앱 없음', 'neutral'], preparing: ['메일 읽기 앱 준비 중', 'warn'], ready: ['메일 읽기 앱 준비됨', 'ok'] };

/** 게이트웨이 상태 — 메일 읽기 앱(Exchange 등록에 쓸 id), 마지막 조회, 오류. 사람별 내용은 없다 */
function GatewayStatus({ gw }) {
  const [label, tone] = READER[gw.reader.state];
  const g = gw.gateway;
  const line = { fontSize: 12, color: 'var(--ink-2)', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' };
  return (
    <div data-mail-gateway-status={gw.reader.state} style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-ctl)', padding: '10px 12px', margin: '0 0 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={line}>
        <Pill tone={tone} dot>{label}</Pill>
        {gw.reader.appId && <span className="mono" style={{ fontSize: 11 }}>앱 id {gw.reader.appId} · 엔터프라이즈 앱 개체 id {gw.reader.servicePrincipalId}</span>}
        {gw.reader.secretExpiresAt && <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>비밀키 만료 {T.dateTime(gw.reader.secretExpiresAt)}(자동 교체)</span>}
      </div>
      {gw.reader.permissionsFound && <div style={{ ...line, color: 'var(--crit)' }}>이 앱에 Entra API 권한이 붙어 있습니다 — 대상 그룹 밖 사서함까지 읽게 되므로 Entra에서 제거해 주세요.</div>}
      {gw.reader.lastError && <div style={{ ...line, color: 'var(--crit)' }}>{gw.reader.lastError}</div>}
      <div style={line} data-mail-calendar-writer={gw.calendarWriter.state}>
        <Pill tone={WRITER[gw.calendarWriter.state][1]} dot>{WRITER[gw.calendarWriter.state][0]}</Pill>
        {gw.calendarWriter.appId && <span className="mono" style={{ fontSize: 11 }}>앱 id {gw.calendarWriter.appId} · 엔터프라이즈 앱 개체 id {gw.calendarWriter.servicePrincipalId}</span>}
        {gw.calendarFailed > 0 && <span style={{ color: 'var(--crit)' }}>캘린더 등록 실패 {gw.calendarFailed}건</span>}
      </div>
      {gw.calendarWriter.permissionsFound && <div style={{ ...line, color: 'var(--crit)' }}>일정 쓰기 앱에 Entra API 권한이 붙어 있습니다 — 대상 그룹 밖 캘린더까지 닿게 되므로 Entra에서 제거해 주세요.</div>}
      {gw.calendarWriter.lastError && <div style={{ ...line, color: 'var(--crit)' }}>{gw.calendarWriter.lastError}</div>}
      <div style={line}>
        {g ? <>마지막 조회 {T.dateTime(g.lastPollAt)} · 읽는 사람 {g.users}명 · 가져갈 메일 {gw.waitingMails}건{gw.failingUsers ? ` · 조회 실패 ${gw.failingUsers}명` : ''}</>
          : <>게이트웨이가 아직 돌지 않았습니다</>}
        <span style={{ color: 'var(--ink-3)' }} data-mail-agent-configured={gw.agent?.configured ? 'yes' : 'no'}>
          · 에이전트 {gw.agent?.configured ? `${gw.agent.agentId}(${gw.agent.quality}) — 24시간 처리 ${gw.runs24h.done}건${gw.runs24h.failed ? ` · 실패 ${gw.runs24h.failed}건` : ''}`
            : `미설정(${!gw.agent?.apiKey ? 'agent-engine 키 없음' : '키 id 없음'}) — 메일을 맡기지 않습니다`}</span>
      </div>
      {g?.lastError && <div style={{ ...line, color: 'var(--crit)' }}>{g.lastError}</div>}
    </div>
  );
}
